import type {
  AssistantMessageTextRevealConfig,
  AssistantResponseKind,
} from '../types';

export type AssistantTextRevealApplyTo = 'live-only' | 'all' | 'none';
export type AssistantTextRevealReplayBehavior = 'settled' | 'animate';
export type AssistantTextRevealReducedMotionBehavior = 'settled' | 'animate';

export type AssistantTextRevealPolicyConfig = AssistantMessageTextRevealConfig & {
  enabled?: boolean;
  applyTo?: AssistantTextRevealApplyTo;
  replayBehavior?: AssistantTextRevealReplayBehavior;
  reducedMotionBehavior?: AssistantTextRevealReducedMotionBehavior;
};

export type AssistantResponseRenderPolicy = {
  schemaVersion: '1.0.0';
  textReveal?: AssistantTextRevealPolicyConfig & {
    perResponseKind?: Partial<Record<AssistantResponseKind, AssistantTextRevealPolicyConfig>>;
  };
};

export type ResolvedAssistantTextRevealConfig = {
  mode: 'text-first';
  startDelayMs: number;
  wordDelayMs: number;
  cursor: boolean;
  revealStructuredSections: boolean;
  structuredHandoffDelayMs: number;
  structuredFadeMs: number;
  applyTo: AssistantTextRevealApplyTo;
  replayBehavior: AssistantTextRevealReplayBehavior;
  reducedMotionBehavior: AssistantTextRevealReducedMotionBehavior;
};

export const DEFAULT_ASSISTANT_RESPONSE_RENDER_POLICY: AssistantResponseRenderPolicy = {
  schemaVersion: '1.0.0',
  textReveal: {
    enabled: true,
    mode: 'text-first',
    startDelayMs: 180,
    wordDelayMs: 34,
    cursor: true,
    revealStructuredSections: true,
    structuredHandoffDelayMs: 120,
    structuredFadeMs: 160,
    applyTo: 'live-only',
    replayBehavior: 'settled',
    reducedMotionBehavior: 'settled',
  },
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const normalizeNonNegativeNumber = (
  value: unknown,
  fallback: number,
  minimum = 0,
): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= minimum ? numeric : fallback;
};

const normalizeBoolean = (value: unknown, fallback: boolean): boolean => (
  typeof value === 'boolean' ? value : fallback
);

const normalizeApplyTo = (value: unknown, fallback: AssistantTextRevealApplyTo): AssistantTextRevealApplyTo => (
  value === 'all' || value === 'none' || value === 'live-only' ? value : fallback
);

const normalizeReplayBehavior = (
  value: unknown,
  fallback: AssistantTextRevealReplayBehavior,
): AssistantTextRevealReplayBehavior => (
  value === 'animate' || value === 'settled' ? value : fallback
);

const normalizeReducedMotionBehavior = (
  value: unknown,
  fallback: AssistantTextRevealReducedMotionBehavior,
): AssistantTextRevealReducedMotionBehavior => (
  value === 'animate' || value === 'settled' ? value : fallback
);

const normalizePolicy = (policy: unknown): AssistantResponseRenderPolicy => {
  if (!isRecord(policy) || !isRecord(policy.textReveal)) {
    return DEFAULT_ASSISTANT_RESPONSE_RENDER_POLICY;
  }
  return {
    schemaVersion: '1.0.0',
    textReveal: {
      ...DEFAULT_ASSISTANT_RESPONSE_RENDER_POLICY.textReveal,
      ...policy.textReveal,
      perResponseKind: isRecord(policy.textReveal.perResponseKind)
        ? policy.textReveal.perResponseKind as Partial<Record<AssistantResponseKind, AssistantTextRevealPolicyConfig>>
        : DEFAULT_ASSISTANT_RESPONSE_RENDER_POLICY.textReveal?.perResponseKind,
    },
  };
};

const normalizeConfig = (
  value: unknown,
  fallback: ResolvedAssistantTextRevealConfig,
): ResolvedAssistantTextRevealConfig | null => {
  const rawConfig = isRecord(value) ? value : null;
  if (rawConfig && rawConfig.enabled === false) {
    return null;
  }
  return {
    mode: 'text-first',
    startDelayMs: normalizeNonNegativeNumber(rawConfig?.startDelayMs, fallback.startDelayMs),
    wordDelayMs: normalizeNonNegativeNumber(rawConfig?.wordDelayMs, fallback.wordDelayMs, 12),
    cursor: normalizeBoolean(rawConfig?.cursor, fallback.cursor),
    revealStructuredSections: normalizeBoolean(
      rawConfig?.revealStructuredSections,
      fallback.revealStructuredSections,
    ),
    structuredHandoffDelayMs: normalizeNonNegativeNumber(
      rawConfig?.structuredHandoffDelayMs,
      fallback.structuredHandoffDelayMs,
    ),
    structuredFadeMs: normalizeNonNegativeNumber(rawConfig?.structuredFadeMs, fallback.structuredFadeMs),
    applyTo: normalizeApplyTo(rawConfig?.applyTo, fallback.applyTo),
    replayBehavior: normalizeReplayBehavior(rawConfig?.replayBehavior, fallback.replayBehavior),
    reducedMotionBehavior: normalizeReducedMotionBehavior(
      rawConfig?.reducedMotionBehavior,
      fallback.reducedMotionBehavior,
    ),
  };
};

export const resolveAssistantTextRevealConfig = ({
  metadata,
  responseKind,
  policy,
  defaultEnabled = false,
}: {
  metadata?: unknown;
  responseKind?: AssistantResponseKind | null;
  policy?: unknown;
  defaultEnabled?: boolean;
}): ResolvedAssistantTextRevealConfig | null => {
  const normalizedPolicy = normalizePolicy(policy);
  const base = normalizeConfig(
    normalizedPolicy.textReveal,
    {
      mode: 'text-first',
      startDelayMs: 180,
      wordDelayMs: 34,
      cursor: true,
      revealStructuredSections: true,
      structuredHandoffDelayMs: 120,
      structuredFadeMs: 160,
      applyTo: 'live-only',
      replayBehavior: 'settled',
      reducedMotionBehavior: 'settled',
    },
  );
  if (!base || base.applyTo === 'none') {
    return null;
  }

  const kindOverride = responseKind && isRecord(normalizedPolicy.textReveal?.perResponseKind)
    ? normalizedPolicy.textReveal.perResponseKind?.[responseKind]
    : undefined;
  const withKindOverride = kindOverride ? normalizeConfig(kindOverride, base) : base;
  if (!withKindOverride || withKindOverride.applyTo === 'none') {
    return null;
  }

  const metadataRecord = isRecord(metadata) ? metadata : null;
  const rawMetadataConfig = isRecord(metadataRecord?.textReveal) ? metadataRecord.textReveal : null;
  if (!rawMetadataConfig && !defaultEnabled) {
    return null;
  }
  return normalizeConfig(rawMetadataConfig ?? withKindOverride, withKindOverride);
};

export const buildAssistantTextRevealMetadata = ({
  responseKind,
  policy,
}: {
  responseKind?: AssistantResponseKind | null;
  policy?: unknown;
}): AssistantMessageTextRevealConfig | null => {
  const resolved = resolveAssistantTextRevealConfig({
    responseKind,
    policy,
    defaultEnabled: true,
  });
  if (!resolved) {
    return null;
  }
  return {
    mode: resolved.mode,
    startDelayMs: resolved.startDelayMs,
    wordDelayMs: resolved.wordDelayMs,
    cursor: resolved.cursor,
    revealStructuredSections: resolved.revealStructuredSections,
    structuredHandoffDelayMs: resolved.structuredHandoffDelayMs,
    structuredFadeMs: resolved.structuredFadeMs,
    applyTo: resolved.applyTo,
    replayBehavior: resolved.replayBehavior,
    reducedMotionBehavior: resolved.reducedMotionBehavior,
  };
};

export const collectAssistantPresentationRevealText = (presentation: {
  sections?: Array<{ type?: unknown; text?: unknown }>;
} | null | undefined): string => {
  if (!presentation?.sections) {
    return '';
  }
  return presentation.sections
    .filter((section) => section.type === 'summary' || section.type === 'formatted_body')
    .map((section) => String(section.text ?? '').trim())
    .filter((value) => value.length > 0)
    .join('\n\n')
    .trim();
};
